import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { GaugeBar } from '../../components/charts/GaugeBar';
import { formatDateTime } from '../../i18n';
import type { SlaStatus } from './api';
import { useSlaEvents } from './queries';
import { SlaBadge } from './SlaBadge';

/**
 * Der Stand der SLA-Uhr am Ticket -- F4-08, K-118.
 *
 * Beide Uhren nebeneinander: Reaktion und Loesung sind getrennte Zusagen, und
 * die eine kann eingehalten und die andere gerissen sein.
 *
 * **Unter jeder Uhr eine Bahn.** "Noch zwei Stunden" heisst bei einer
 * Reaktionszeit von vier Stunden etwas anderes als bei einer Loesungszeit von
 * fuenf Tagen -- die Zahl allein sagt nicht, ob es knapp wird. Die Bahn zeigt
 * das Verhaeltnis, und der Strich darin sagt, ab wann der Waechter warnt.
 * Beides kommt vom Server: Die Frist laeuft in Geschaeftszeit, und die kennt
 * nur der Kalender.
 *
 * Darunter der Verlauf aus `sla_events`. Er beantwortet die Frage, die in
 * einer Eskalation gestellt wird -- "wann wurde gewarnt, wann ist die Frist
 * gerissen?". Eine Warnung, die nur eine Mail war, ist nach einem Jahr nicht
 * mehr nachweisbar.
 */
export function TicketSlaCard({ ticketId, sla }: { ticketId: string; sla: SlaStatus | null }) {
  const { t, i18n } = useTranslation();
  const events = useSlaEvents(ticketId, Boolean(sla?.sla_id));

  if (!sla?.sla_id) {
    return null;
  }

  return (
    <>
      <h2 style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>{t('sla.clock')}</h2>

      <dl className="definition-list">
        <dt>{t('sla.sla')}</dt>
        <dd>
          {sla.sla_name ?? '—'}
          {sla.is_paused ? (
            <>
              {' '}
              <Badge tone="neutral">{t('sla.paused')}</Badge>
            </>
          ) : null}
        </dd>

        <dt>{t('sla.firstResponse')}</dt>
        <dd>
          <SlaBadge sla={sla} target="first_response" />
          {sla.first_response_due_at ? (
            <div className="table__muted">
              {t('sla.dueAt', {
                moment: formatDateTime(sla.first_response_due_at, i18n.language),
              })}
            </div>
          ) : null}
          <Elapsed
            label={t('sla.firstResponse')}
            total={sla.first_response_total_seconds}
            remaining={sla.first_response_remaining_seconds}
            threshold={sla.warn_at_percent}
            paused={sla.is_paused}
          />
        </dd>

        <dt>{t('sla.resolution')}</dt>
        <dd>
          <SlaBadge sla={sla} />
          {sla.resolution_due_at ? (
            <div className="table__muted">
              {t('sla.dueAt', { moment: formatDateTime(sla.resolution_due_at, i18n.language) })}
            </div>
          ) : null}
          <Elapsed
            label={t('sla.resolution')}
            total={sla.resolution_total_seconds}
            remaining={sla.resolution_remaining_seconds}
            threshold={sla.warn_at_percent}
            paused={sla.is_paused}
          />
        </dd>
      </dl>

      {events.data && events.data.length > 0 ? (
        <ul className="stack" style={{ marginTop: 'var(--space-2)' }}>
          {events.data.map((event) => (
            <li key={event.id} className="table__muted">
              {formatDateTime(event.occurred_at, i18n.language)} ·{' '}
              {t(`slaEvent.${event.event_type}`)}
              {event.target_type ? ` (${t(`slaTarget.${event.target_type}`)})` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/**
 * Die verbrauchte Frist als Bahn.
 *
 * Erscheint nur, solange es etwas zu zeigen gibt: Ist die Frist beantwortet
 * oder erfuellt, faellt die Restzeit weg -- und eine Bahn ohne Rest waere
 * eine leere Aussage neben einer Kennzeichnung, die sie schon macht.
 *
 * Ueber hundert Prozent wird nicht hinausgezeichnet. Wie weit die Frist
 * ueberschritten ist, steht als Restzeit daneben; eine Bahn, die aus ihrem
 * Rahmen laeuft, sagt dasselbe schlechter.
 */
function Elapsed({
  label,
  total,
  remaining,
  threshold,
  paused,
}: {
  label: string;
  total: number | null | undefined;
  remaining: number | null | undefined;
  threshold: number | null | undefined;
  paused: boolean;
}) {
  const { t } = useTranslation();

  if (!total || remaining === null || remaining === undefined) return null;

  const used = Math.min(100, Math.max(0, ((total - remaining) / total) * 100));
  const warn = threshold ?? 80;
  const tone = paused ? 'neutral' : remaining < 0 ? 'danger' : used >= warn ? 'warning' : 'success';

  return (
    <div style={{ marginTop: 'var(--space-2)' }}>
      <GaugeBar
        label={t('sla.progress')}
        // Sichtbar steht nur "Verbrauchte Frist" -- welche Frist gemeint
        // ist, sagt die Zeile darueber. Wer die Seite hoert, hat diese
        // Umgebung nicht und bekommt sie hier dazu.
        caption={`${t('sla.progress')} · ${label}`}
        percent={used}
        display={`${String(Math.round(used))} %`}
        threshold={warn}
        tone={tone}
      />
    </div>
  );
}
